import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { Icon } from '../../../icon/src';
import { Text } from '../../../typography/src';
const sizeClasses = {
    xs: 'nucleus-px-1 nucleus-py-1 nucleus-min-h-8',
    sm: 'nucleus-px-4 nucleus-py-1 nucleus-min-h-8',
    base: 'nucleus-px-4 nucleus-py-2 nucleus-min-h-10',
    full: 'nucleus-w-full nucleus-h-full',
};
// Accessibility best practices for button: https://www.w3.org/WAI/ARIA/apg/patterns/button/
/**
 Buttons allow a user to take an action, whether it is confirming the submission of a form, or cancelling out of a dialog, with a single click or key press.
 @see {@link https://storybook.medable.tech/nucleus-components/?path=/docs/button--primary} for Storybook documentation
 @see {@link https://www.figma.com/file/hT1QbhSTM1FlsNJ2HXSSvQ/Nucleus-Design-System?node-id=6%3A2490} for Nucleus
    design
*/
export const Button = forwardRef(function Button(_a, ref) {
    var _b;
    var { label, dataTestId, disabled = false, size = 'base', variant = 'primary', content, isActive, className, iconName, iconPosition = 'end' } = _a, buttonProps = __rest(_a, ["label", "dataTestId", "disabled", "size", "variant", "content", "isActive", "className", "iconName", "iconPosition"]);
    const ariaLabel = (_b = buttonProps['aria-label']) !== null && _b !== void 0 ? _b : label;
    const iconColor = variant === 'primary' || variant === 'destructive'
        ? 'white'
        : variant === 'secondary-destructive'
            ? 'red'
            : 'black';
    return (_jsx("button", Object.assign({ ref: ref, "aria-disabled": disabled, "aria-label": ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : '', className: `nucleus-box-border nucleus-inline-flex nucleus-max-w-full nucleus-cursor-pointer nucleus-items-center nucleus-justify-center nucleus-border-solid nucleus-bg-none nucleus-font-medium nucleus-normal-case nucleus-outline-0 nucleus-transition-colors nucleus-m-0 nucleus-rounded-md
         focus-visible:nucleus-ring-2 focus-visible:nucleus-ring-offset-2 focus-visible:nucleus-ring-offset-white disabled:nucleus-pointer-events-none disabled:nucleus-text-text-disabled ${sizeClasses[size]} 
         ${getBackgroundColor(variant, isActive)}
         ${{
            'secondary': 'nucleus-border nucleus-border-neutral-300 nucleus-text-text-primary hover:nucleus-bg-neutral-50 active:nucleus-bg-neutral-300 disabled:nucleus-bg-neutral-200 disabled:bor ' +
                'focus-visible:nucleus-ring-primary-500 disabled:nucleus-border-0 nucleus-shadow-sm',
            'secondary-destructive': 'nucleus-border nucleus-border-neutral-300 nucleus-text-text-error hover:nucleus-bg-red-50 active:nucleus-bg-neutral-300 disabled:nucleus-bg-neutral-200 ' +
                'focus-visible:nucleus-ring-red-500 disabled:nucleus-border-0 nucleus-shadow-sm',
            'transparent': 'nucleus-border-0 nucleus-text-text-action hover:nucleus-bg-primary-100 active:nucleus-bg-primary-100 focus-visible:nucleus-ring-primary-500 ' +
                'disabled:nucleus-bg-transparent',
            'destructive': 'nucleus-border-0 nucleus-text-white hover:nucleus-bg-red-700 active:nucleus-bg-red-800 disabled:nucleus-bg-neutral-200 focus-visible:nucleus-ring-red-700 nucleus-shadow-sm',
            'primary': 'nucleus-border-0 hover:nucleus-bg-primary-700 active:nucleus-bg-primary-800 disabled:nucleus-bg-neutral-200 nucleus-text-white focus-visible:nucleus-ring-primary-500 nucleus-shadow-sm',
        }[variant]} ${className !== null && className !== void 0 ? className : ''}`, "data-testid": dataTestId, disabled: disabled }, buttonProps, { children: _jsx("div", { className: 'nucleus-max-w-full nucleus-truncate nucleus-box-border nucleus-flex', children: !content ? (_jsxs(Text, { as: 'span', size: 'sm', className: 'flex items-center gap-2', children: [iconName && iconPosition === 'start' && _jsx(Icon, { name: iconName, size: 'sm', color: iconColor }), label, iconName && iconPosition === 'end' && _jsx(Icon, { name: iconName, size: 'sm', color: iconColor })] })) : (content) }) })));
});
/**
 * This function is used to determine if the button is active or not. If the button is active, the background color
 * will change.
 */
export const getBackgroundColor = (variant, isActive) => {
    if (isActive) {
        switch (variant) {
            case 'primary':
                return 'nucleus-bg-primary-800';
            case 'secondary':
                return 'nucleus-bg-neutral-300';
            case 'secondary-destructive':
                return 'nucleus-bg-neutral-300';
            case 'destructive':
                return 'nucleus-bg-red-800';
            case 'transparent':
                return 'nucleus-bg-primary-100';
        }
    }
    switch (variant) {
        case 'primary':
            return 'nucleus-bg-primary-500';
        case 'secondary':
            return 'nucleus-bg-white';
        case 'secondary-destructive':
            return 'nucleus-bg-white';
        case 'destructive':
            return 'nucleus-bg-red-600';
        case 'transparent':
            return 'nucleus-bg-transparent';
    }
};
//# sourceMappingURL=button.js.map